<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <div class="fin-header fin-toolbar">
      <span class="fin-title">{{ $t("finance.reports.pageTitle") }}</span>
    </div>
    <v-tabs v-model="activeTab" color="white" slider-color="primary">
      <v-tab>{{ $t("finance.reports.trialBalance") }}</v-tab>
      <v-tab>{{ $t("finance.reports.captainStatement") }}</v-tab>
      <v-tab>{{ $t("finance.reports.monthlySettlements") }}</v-tab>

      <v-tab-item>
        <v-card class="mt-3">
          <v-card-text class="pa-2">
            <div class="fin-filters">
              <v-text-field v-model="trialBalanceFilters.from" type="date" :label="$t('finance.common.from')" hide-details class="fin-field"></v-text-field>
              <v-text-field v-model="trialBalanceFilters.to" type="date" :label="$t('finance.common.to')" hide-details class="fin-field"></v-text-field>
              <v-btn small color="primary" @click="loadTrialBalance">{{ $t("finance.reports.loadTrialBalance") }}</v-btn>
              <v-btn small flat @click="downloadTrialBalance('csv')">{{ $t("finance.common.csv") }}</v-btn>
              <v-btn small flat @click="downloadTrialBalance('pdf')">{{ $t("finance.common.pdf") }}</v-btn>
              <v-btn small flat color="primary" :to="{ name: 'TrialBalance' }">
                <v-icon small class="me-1">account_tree</v-icon>
                {{ $t("finance.trialBalance.openFullReport") }}
              </v-btn>
            </div>
          </v-card-text>
          <v-data-table class="fin-dt" :headers="trialHeaders" :items="trialBalance.lines || []" :rows-per-page-items="[10, 25, 50, 100]">
            <template v-slot:items="props">
              <tr>
                <td class="fin-code">{{ props.item.code }}</td>
                <td>{{ localizedAccountName(props.item) }}</td>
                <td>{{ formatAccountType(props.item.type) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.debit_total) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.credit_total) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.balance) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card class="mt-3">
          <v-card-text class="pa-2">
            <div class="fin-filters">
              <choose-captains-multi :multi="false" :label="$t('finance.common.captain')" v-model="statementFilters.captain_id" class="fin-field--wide" />
              <v-text-field v-model="statementFilters.from" type="date" :label="$t('finance.common.from')" hide-details class="fin-field"></v-text-field>
              <v-text-field v-model="statementFilters.to" type="date" :label="$t('finance.common.to')" hide-details class="fin-field"></v-text-field>
              <v-btn small color="primary" @click="loadCaptainStatement">{{ $t("finance.common.load") }}</v-btn>
              <v-btn small flat @click="downloadCaptainStatement('csv')">{{ $t("finance.common.csv") }}</v-btn>
              <v-btn small flat @click="downloadCaptainStatement('pdf')">{{ $t("finance.common.pdf") }}</v-btn>
            </div>

            <div v-if="captainStatement.summary" class="fin-summary">
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.reports.driverAccountCode") }}</span>
                <span class="fin-stat-value">{{ captainStatement.account_code || '-' }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.reports.bookingRevenue") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.booking_revenue) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.common.bonus") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.bonus) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.common.penalty") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.penalty) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.common.hrDeduction") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.hr_deduction) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.reports.settledToDriver") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.settled_to_driver) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.reports.receivedFromDriver") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.received_from_driver) }}</span>
              </div>
              <div class="fin-stat">
                <span class="fin-stat-label">{{ $t("finance.common.net") }}</span>
                <span class="fin-stat-value">{{ formatAmount(captainStatement.summary.net_balance) }}</span>
              </div>
            </div>
          </v-card-text>
          <v-data-table class="fin-dt" :headers="statementHeaders" :items="captainStatement.lines || []" :rows-per-page-items="[10, 25, 50, 100]">
            <template v-slot:items="props">
              <tr>
                <td>{{ formatDate(props.item.posted_at) }}</td>
                <td>{{ formatEntryType(props.item.entry_type) }}</td>
                <td>{{ props.item.adjustment_type || '-' }}</td>
                <td class="fin-num">{{ formatAmount(props.item.credit_amount) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.debit_amount) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.balance_after) }}</td>
                <td>{{ props.item.note || '-' }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card class="mt-3">
          <v-card-text class="pa-2">
            <div class="fin-filters">
              <v-text-field v-model="monthlySettlementMonth" type="month" :label="$t('finance.common.month')" hide-details class="fin-field"></v-text-field>
              <v-btn small color="primary" @click="loadMonthlySettlements">{{ $t("finance.common.load") }}</v-btn>
              <v-btn small flat @click="downloadMonthlySettlements('csv')">{{ $t("finance.common.csv") }}</v-btn>
              <v-btn small flat @click="downloadMonthlySettlements('pdf')">{{ $t("finance.common.pdf") }}</v-btn>
            </div>
          </v-card-text>
          <v-data-table class="fin-dt" :headers="monthlyHeaders" :items="monthlySettlements.rows || []" :rows-per-page-items="[10, 25, 50, 100]">
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.captain_id }}</td>
                <td>{{ props.item.captain_name }}</td>
                <td class="fin-code">{{ props.item.account_code || '-' }}</td>
                <td>{{ props.item.phone }}</td>
                <td class="fin-num">{{ formatAmount(props.item.booking_revenue) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.bonus_total) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.penalty_total) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.hr_deduction_total) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.settled_to_driver) }}</td>
                <td class="fin-num">{{ formatAmount(props.item.month_net_balance) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs>
  </div>
</template>

<script>
import { formatFinanceDate } from "@/mixins/financeDate";
import ChooseCaptainsMulti from "@/components/monaco/input/ChooseCaptainMulti";
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "FinanceReports",
  title() {
    return this.$t("finance.reports.pageTitle");
  },
  components: { ChooseCaptainsMulti, FinanceSectionNav, LoadingComponent },
  data() {
    return {
      activeTab: 0,
      loading: false,
      monthlySettlementMonth: null,
      trialBalance: { lines: [] },
      captainStatement: { lines: [], summary: null },
      monthlySettlements: { rows: [] },
      trialBalanceFilters: {
        from: null,
        to: null,
      },
      statementFilters: {
        captain_id: null,
        from: null,
        to: null,
      },
    };
  },
  computed: {
    trialHeaders() {
      return [
        { text: this.$t("finance.common.code"), value: "code" },
        { text: this.$t("finance.common.account"), value: "name" },
        { text: this.$t("finance.common.type"), value: "type" },
        { text: this.$t("finance.common.debit"), value: "debit_total", align: "end" },
        { text: this.$t("finance.common.credit"), value: "credit_total", align: "end" },
        { text: this.$t("finance.common.balance"), value: "balance", align: "end" },
      ];
    },
    statementHeaders() {
      return [
        { text: this.$t("finance.common.date"), value: "posted_at" },
        { text: this.$t("finance.reports.entry"), value: "entry_type" },
        { text: this.$t("finance.ledger.adjustment"), value: "adjustment_type" },
        { text: this.$t("finance.common.credit"), value: "credit_amount", align: "end" },
        { text: this.$t("finance.common.debit"), value: "debit_amount", align: "end" },
        { text: this.$t("finance.common.balance"), value: "balance_after", align: "end" },
        { text: this.$t("finance.common.note"), value: "note" },
      ];
    },
    monthlyHeaders() {
      return [
        { text: this.$t("finance.reports.captainId"), value: "captain_id" },
        { text: this.$t("finance.common.captain"), value: "captain_name" },
        { text: this.$t("finance.reports.driverAccountCode"), value: "account_code" },
        { text: this.$t("finance.common.phone"), value: "phone" },
        { text: this.$t("finance.common.revenue"), value: "booking_revenue", align: "end" },
        { text: this.$t("finance.common.bonus"), value: "bonus_total", align: "end" },
        { text: this.$t("finance.common.penalty"), value: "penalty_total", align: "end" },
        { text: this.$t("finance.common.hrDeduction"), value: "hr_deduction_total", align: "end" },
        { text: this.$t("finance.common.settled"), value: "settled_to_driver", align: "end" },
        { text: this.$t("finance.common.net"), value: "month_net_balance", align: "end" },
      ];
    },
  },
  mounted() {
    this.loadTrialBalance();
    this.loadMonthlySettlements();
  },
  methods: {
    formatAccountType(value) {
      const map = {
        ASSET: this.$t("finance.coa.typeAsset"),
        LIABILITY: this.$t("finance.coa.typeLiability"),
        EQUITY: this.$t("finance.coa.typeEquity"),
        REVENUE: this.$t("finance.coa.typeRevenue"),
        EXPENSE: this.$t("finance.coa.typeExpense"),
      };
      return map[value] || value;
    },
    formatEntryType(value) {
      const map = {
        debit: this.$t("finance.journals.debit"),
        credit: this.$t("finance.journals.credit"),
      };
      return map[value] || value;
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    loadTrialBalance() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_TRIAL_BALANCE, {
          params: this.trialBalanceFilters,
        })
        .then(
          function (response) {
            this.trialBalance = response.data.data || { lines: [] };
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
                this.getErrorMessage(error, this.$t("finance.reports.failedLoadTrialBalance")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    downloadTrialBalance(format) {
      this.downloadFile(
        this.api.ACTIONS.ACCOUNTING_TRIAL_BALANCE,
        {
          from: this.trialBalanceFilters.from,
          to: this.trialBalanceFilters.to,
          format: format,
        },
        "trial-balance." + format
      );
    },
    loadCaptainStatement() {
      if (!this.statementFilters.captain_id) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.selectCaptainFirst"), "red");
        return;
      }

      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CAPTAIN_STATEMENT(this.statementFilters.captain_id), {
          params: {
            from: this.statementFilters.from,
            to: this.statementFilters.to,
          },
        })
        .then(
          function (response) {
            this.captainStatement = response.data.data || { lines: [], summary: null };
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedLoadCaptainStatement")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    downloadCaptainStatement(format) {
      if (!this.statementFilters.captain_id) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.selectCaptainFirst"), "red");
        return;
      }

      this.downloadFile(
        this.api.ACTIONS.ACCOUNTING_CAPTAIN_STATEMENT(this.statementFilters.captain_id),
        {
          from: this.statementFilters.from,
          to: this.statementFilters.to,
          format: format,
        },
        "captain-statement." + format
      );
    },
    loadMonthlySettlements() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_MONTHLY_SETTLEMENTS, {
          params: {
            month: this.monthlySettlementMonth,
          },
        })
        .then(
          function (response) {
            this.monthlySettlements = response.data.data || { rows: [] };
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedLoadMonthlySettlements")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    downloadMonthlySettlements(format) {
      this.downloadFile(
        this.api.ACTIONS.ACCOUNTING_MONTHLY_SETTLEMENTS,
        {
          month: this.monthlySettlementMonth,
          format: format,
        },
        "monthly-settlements." + format
      );
    },
    downloadFile(url, params, filename) {
      this.loading = true;
      this.axios
        .get(url, {
          params: params,
          responseType: "blob",
        })
        .then(function (response) {
          var blob = new Blob([response.data], { type: response.headers["content-type"] || "application/octet-stream" });
          var blobUrl = window.URL.createObjectURL(blob);
          var link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedDownloadReport")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    formatAmount(value) {
      return this.formatMoney(value);
    },
    localizedAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    formatDate(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
  },
};
</script>